// Domain detail + settings page (MIS-809 / Task 28).
//
// Loads `GET /api/domains/:id` → `{ domain, validationRecords }`. Shows the host
// (read-only — Cloudflare can't rename a hostname) and current status badge, then
// an editable settings form (react-hook-form + zod): brand name, support email,
// and the three default phone numbers (E.164). Save → PATCH; Delete → confirm
// dialog → DELETE → back to the list.
//
// API reality (apps/worker/src/routes/domains.ts): PATCH requires the `editor`
// domain role and accepts brandName / supportEmail / defaultPhone{Premium,
// Standard,Human}. The affiliate slug (`affiliateSlug`) is NOT patchable here, so
// it is shown read-only.
//
// Puck-7 (MIS-980): domain-wide tracking — Meta Pixel ID + GTM container ID. The
// domain row carries `fbPixelId`/`gtmId` (Puck-1), the domains PATCH schema
// accepts them (apps/worker/src/routes/domains.ts patchSchema), and the Puck
// renderer emits the pixel from these domain values. Pixel is per-DOMAIN here,
// never per-lander — all of a domain's landers share it.

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { DomainStatusBadge } from '@/components/domains/DomainStatusBadge';
import { formatTimestamp } from '@/components/domains/format';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';

export const Route = createFileRoute('/_authenticated/domains/$domainId/')({
  component: DomainDetailPage,
});

// E.164: optional leading +, then 7–15 digits. Empty string is allowed (the
// field is optional and cleared values are simply not sent).
const phoneField = z
  .string()
  .regex(/^\+?[1-9]\d{6,14}$/, 'Use E.164 format, e.g. +12155551234')
  .or(z.literal(''));

const formSchema = z.object({
  brandName: z.string().max(200).or(z.literal('')),
  supportEmail: z.email('Enter a valid email').or(z.literal('')),
  defaultPhonePremium: phoneField,
  defaultPhoneStandard: phoneField,
  defaultPhoneHuman: phoneField,
  // Puck-7: domain-wide tracking IDs (rendered into every lander on this domain).
  fbPixelId: z.string().max(64, 'Pixel ID is too long').or(z.literal('')),
  gtmId: z.string().max(64, 'GTM ID is too long').or(z.literal('')),
});

type FormValues = z.infer<typeof formSchema>;

/** Coerce the route's `unknown` validationRecords into a list of strings. */
function validationMessages(records: unknown): string[] {
  if (!Array.isArray(records)) return [];
  return records.map((r) => {
    if (typeof r === 'string') return r;
    if (r && typeof r === 'object') {
      const obj = r as Record<string, unknown>;
      const txt = obj.txt_name ?? obj.txt_value ?? obj.status ?? JSON.stringify(r);
      return String(txt);
    }
    return String(r);
  });
}

function DomainDetailPage() {
  const { domainId } = Route.useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['domains', 'detail', domainId],
    queryFn: async () => {
      const res = await api.api.domains[':domainId'].$get({ param: { domainId } });
      if (!res.ok) throw new Error(`Failed to load domain (${res.status})`);
      return res.json();
    },
  });

  const domain = data?.domain;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      brandName: '',
      supportEmail: '',
      defaultPhonePremium: '',
      defaultPhoneStandard: '',
      defaultPhoneHuman: '',
      fbPixelId: '',
      gtmId: '',
    },
  });

  // Populate the form once the domain loads (and whenever it changes).
  useEffect(() => {
    if (!domain) return;
    reset({
      brandName: domain.brandName ?? '',
      supportEmail: domain.supportEmail ?? '',
      defaultPhonePremium: domain.defaultPhonePremium ?? '',
      defaultPhoneStandard: domain.defaultPhoneStandard ?? '',
      defaultPhoneHuman: domain.defaultPhoneHuman ?? '',
      fbPixelId: domain.fbPixelId ?? '',
      gtmId: domain.gtmId ?? '',
    });
  }, [domain, reset]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      // The PATCH route parses the body with `c.req.json()` (no zValidator), so
      // the RPC client exposes no typed `json` field for it. Send the JSON body
      // through the typed `init` escape hatch instead; useApi()'s fetch preserves
      // the body + Content-Type and adds the auth / X-Tenant-Id headers.
      const body: Record<string, string> = {};
      if (values.brandName) body.brandName = values.brandName;
      if (values.supportEmail) body.supportEmail = values.supportEmail;
      if (values.defaultPhonePremium) body.defaultPhonePremium = values.defaultPhonePremium;
      if (values.defaultPhoneStandard) body.defaultPhoneStandard = values.defaultPhoneStandard;
      if (values.defaultPhoneHuman) body.defaultPhoneHuman = values.defaultPhoneHuman;
      // Puck-7: domain-wide tracking IDs. NOTE: the worker's domains PATCH schema
      // is currently `.strict()` and does not yet accept these keys — see the
      // file header. They are sent under the correct names so this works once the
      // schema is widened.
      if (values.fbPixelId) body.fbPixelId = values.fbPixelId;
      if (values.gtmId) body.gtmId = values.gtmId;

      const res = await api.api.domains[':domainId'].$patch(
        { param: { domainId } },
        { init: { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } } },
      );
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast({ title: 'Settings saved', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: 'Could not save', description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.domains[':domainId'].$delete({ param: { domainId } });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast({ title: 'Domain removed', variant: 'success' });
      void navigate({ to: '/domains' });
    },
    onError: (err: Error) => {
      toast({ title: 'Could not remove domain', description: err.message, variant: 'destructive' });
    },
  });

  const onSubmit = handleSubmit((values) => saveMutation.mutate(values));

  if (isLoading) {
    return <div className="p-8 text-sm text-gray-500">Loading domain…</div>;
  }
  if (isError || !domain) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-sm text-red-600">Failed to load this domain.</p>
        <Link to="/domains" className="text-sm text-gray-700 underline">
          Back to domains
        </Link>
      </div>
    );
  }

  const validations = validationMessages(data?.validationRecords);
  const lastActivity = domain.activatedAt ?? domain.updatedAt ?? domain.createdAt;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <div>
        <Link to="/domains" className="text-sm text-gray-500 hover:underline">
          ← Domains
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-mono text-xl font-semibold text-gray-900">{domain.host}</h1>
          <DomainStatusBadge domain={domain} validationErrors={validations} />
        </div>
        {lastActivity && (
          <p className="mt-1 text-xs text-gray-400">
            Last activity {formatTimestamp(lastActivity)}
          </p>
        )}
      </div>

      {/* Quick links to the per-domain sub-pages. */}
      <div className="flex flex-wrap gap-2">
        <Link to="/domains/$domainId/landers" params={{ domainId }}>
          <Button variant="outline" size="sm">
            Landers
          </Button>
        </Link>
        <Link to="/domains/$domainId/integrations" params={{ domainId }}>
          <Button variant="outline" size="sm">
            Integrations
          </Button>
        </Link>
        <Link to="/domains/$domainId/analytics" params={{ domainId }}>
          <Button variant="outline" size="sm">
            Analytics
          </Button>
        </Link>
        <Link to="/domains/$domainId/access" params={{ domainId }}>
          <Button variant="outline" size="sm">
            Access
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="brandName">Brand name</Label>
              <Input id="brandName" placeholder="Brand X" {...register('brandName')} />
              {errors.brandName && (
                <p className="text-sm text-red-600">{errors.brandName.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="supportEmail">Support email</Label>
              <Input
                id="supportEmail"
                type="email"
                placeholder="support@brand-x.com"
                {...register('supportEmail')}
              />
              {errors.supportEmail && (
                <p className="text-sm text-red-600">{errors.supportEmail.message}</p>
              )}
            </div>

            {/* Puck-7: domain-wide tracking. Pixel/GTM live on the DOMAIN (not the
                lander) so every page under this host fires the same tags. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="fbPixelId">Meta Pixel ID (domain-wide)</Label>
                <Input
                  id="fbPixelId"
                  placeholder="123456789012345"
                  autoComplete="off"
                  {...register('fbPixelId')}
                />
                {errors.fbPixelId ? (
                  <p className="text-sm text-red-600">{errors.fbPixelId.message}</p>
                ) : (
                  <p className="text-xs text-gray-400">Fires on every lander under this domain.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gtmId">GTM Container ID</Label>
                <Input
                  id="gtmId"
                  placeholder="GTM-XXXXXXX"
                  autoComplete="off"
                  {...register('gtmId')}
                />
                {errors.gtmId && <p className="text-sm text-red-600">{errors.gtmId.message}</p>}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="defaultPhonePremium">Default phone (premium)</Label>
                <Input
                  id="defaultPhonePremium"
                  placeholder="+12155551234"
                  {...register('defaultPhonePremium')}
                />
                {errors.defaultPhonePremium && (
                  <p className="text-sm text-red-600">{errors.defaultPhonePremium.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="defaultPhoneStandard">Default phone (standard)</Label>
                <Input
                  id="defaultPhoneStandard"
                  placeholder="+12155551234"
                  {...register('defaultPhoneStandard')}
                />
                {errors.defaultPhoneStandard && (
                  <p className="text-sm text-red-600">{errors.defaultPhoneStandard.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="defaultPhoneHuman">Default phone (human)</Label>
                <Input
                  id="defaultPhoneHuman"
                  placeholder="+12155551234"
                  {...register('defaultPhoneHuman')}
                />
                {errors.defaultPhoneHuman && (
                  <p className="text-sm text-red-600">{errors.defaultPhoneHuman.message}</p>
                )}
              </div>
            </div>

            {/* Affiliate slug is set elsewhere; the domains PATCH route does not
                accept it, so it's shown read-only here. */}
            <div className="space-y-1.5">
              <Label htmlFor="affiliateSlug">Affiliate publisher slug</Label>
              <Input
                id="affiliateSlug"
                value={domain.affiliateSlug ?? ''}
                readOnly
                disabled
                placeholder="—"
              />
              <p className="text-xs text-gray-400">Managed separately (read-only here).</p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={saveMutation.isPending || !isDirty}>
                {saveMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-700">Danger zone</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            Removing a domain revokes its certificate and deletes its settings.
          </p>
          <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
            Delete domain
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        ariaLabel="Confirm delete domain"
      >
        <DialogHeader>
          <DialogTitle>Remove domain</DialogTitle>
          <DialogDescription>This can't be undone.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-700">
            Remove <span className="font-mono font-medium">{domain.host}</span>?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
